import { useMemo } from 'react';

import { Chip, Typography, makeStyles } from '@material-ui/core';

import type { eventWithTime } from 'rrweb';

import {
  INTERACTION_LABELS,
  InteractionType,
  extractInteractions,
  formatTime,
} from '@spotify/confidence-session-replay-react';

const useStyles = makeStyles({
  root: {
    marginTop: 'var(--bui-space-6)',
    borderRadius: 'var(--bui-radius-2)',
    border: '1px solid var(--bui-border-disabled)',
    overflow: 'hidden',
  },
  header: {
    padding: 'var(--bui-space-4) var(--bui-space-5)',
    borderBottom: '1px solid var(--bui-border-disabled)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 320,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--bui-space-4)',
    padding: 'var(--bui-space-2) var(--bui-space-5)',
    transition: 'background 0.1s',
    cursor: 'pointer',

    '&:hover': {
      background: 'var(--bui-bg-tint-hover)',
    },
  },
  timestamp: {
    fontVariantNumeric: 'tabular-nums',
    minWidth: 48,
  },
});

const DESCRIPTIONS: Record<InteractionType, string> = {
  click: 'Clicked element',
  scroll: 'Scrolled page',
  input: 'Form input',
};

type EventLogProps = {
  events: eventWithTime[];
  onEventClick?: (timeOffset: number) => void;
};

export const EventLog = ({ events, onEventClick }: EventLogProps) => {
  const classes = useStyles();
  const interactions = useMemo(() => extractInteractions(events), [events]);

  if (interactions.length === 0) return null;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle1">Event log</Typography>
      </div>
      <div className={classes.list}>
        {interactions.map((entry, i) => (
          <div
            key={i}
            className={classes.row}
            role="button"
            tabIndex={0}
            onClick={() => onEventClick?.(entry.time)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEventClick?.(entry.time);
              }
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.timestamp}
            >
              {formatTime(entry.time, 1)}
            </Typography>
            <Chip label={INTERACTION_LABELS[entry.type]} size="small" />
            <Typography variant="body2" color="textSecondary">
              {DESCRIPTIONS[entry.type]}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
